/**
 * Offline-first sync service
 *
 * Strategy:
 * 1. localStorage is always the source of truth (works offline)
 * 2. Changes are queued when offline
 * 3. Queue is processed when online via REST API
 * 4. On login, data is merged from cloud
 */

import { api, isApiConfigured } from './api'

// ============================================
// User ID Helper
// ============================================

export function isValidUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(str)
}

export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Get the current user ID synchronously.
 * Reads from sessionStorage where Better Auth session data is cached.
 * Falls back to 'local' for offline-first support.
 */
export function getCurrentUserId(): string {
  try {
    // Better Auth stores session in a cookie, but we cache the user ID in sessionStorage
    const cached = sessionStorage.getItem('waifudo-user-id')
    if (cached && isValidUUID(cached)) {
      return cached
    }
  } catch {
    // sessionStorage not available
  }
  return 'local'
}

/**
 * Set the current user ID (called after login)
 */
export function setCurrentUserId(userId: string): void {
  try {
    sessionStorage.setItem('waifudo-user-id', userId)
  } catch {
    // sessionStorage not available
  }
}

/**
 * Clear the current user ID (called after logout)
 */
export function clearCurrentUserId(): void {
  try {
    sessionStorage.removeItem('waifudo-user-id')
  } catch {
    // sessionStorage not available
  }
}

// Sync queue stored in localStorage
const SYNC_QUEUE_KEY = 'waifudo-sync-queue'
const LAST_SYNC_KEY = 'waifudo-last-sync'

export type SyncOperation = 'create' | 'update' | 'delete'
export type SyncTable = 'tasks' | 'habits' | 'routines' | 'projects' | 'profile'

interface SyncQueueItem {
  id: string
  table: SyncTable
  operation: SyncOperation
  data: Record<string, unknown>
  timestamp: number
}

// ============================================
// Queue Management
// ============================================

function getSyncQueue(): SyncQueueItem[] {
  try {
    const stored = localStorage.getItem(SYNC_QUEUE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function saveSyncQueue(queue: SyncQueueItem[]): void {
  localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue))
}

export function queueSync(
  table: SyncTable,
  operation: SyncOperation,
  id: string,
  data: Record<string, unknown>
): void {
  const queue = getSyncQueue()

  const filtered = queue.filter(
    (item) => !(item.table === table && item.id === id)
  )

  if (operation === 'delete') {
    const wasLocalOnly = queue.some(
      (item) => item.table === table && item.id === id && item.operation === 'create'
    )
    if (wasLocalOnly) {
      saveSyncQueue(filtered)
      return
    }
  }

  filtered.push({
    id,
    table,
    operation,
    data,
    timestamp: Date.now(),
  })

  saveSyncQueue(filtered)

  if (navigator.onLine) {
    processQueue().catch(console.error)
  }
}

/**
 * Process the sync queue by sending batch operations to the API
 */
export async function processQueue(): Promise<{ success: number; failed: number }> {
  if (!isApiConfigured()) {
    return { success: 0, failed: 0 }
  }

  const queue = getSyncQueue()
  if (queue.length === 0) {
    return { success: 0, failed: 0 }
  }

  // Filter valid items
  const validItems = queue.filter((item) => {
    if (!isValidUUID(item.id)) return false
    const userId = item.data.user_id as string
    if (userId === 'local') return false
    if (userId && !isValidUUID(userId)) return false
    return true
  })

  // Keep items with 'local' user_id in queue
  const localItems = queue.filter((item) => {
    const userId = item.data.user_id as string
    return userId === 'local'
  })

  if (validItems.length === 0) {
    saveSyncQueue(localItems)
    return { success: 0, failed: 0 }
  }

  try {
    // Convert to API batch format
    const operations = validItems.map((item) => ({
      table: item.table,
      operation: item.operation,
      id: item.id,
      data: item.data,
    }))

    const result = await api.post<{ success: number; failed: number }>(
      '/api/sync/batch',
      { operations }
    )

    // Keep only local items in queue (items with 'local' user_id)
    saveSyncQueue(localItems)

    if (result.success > 0) {
      localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString())
      console.log(`[Sync] Synced ${result.success} items, ${result.failed} failed`)
    }

    return result
  } catch (error) {
    console.error('[Sync] Batch sync failed:', error)
    // Keep all items in queue on failure
    return { success: 0, failed: validItems.length }
  }
}

// ============================================
// Data Fetching
// ============================================

export async function fetchUserData(_userId: string): Promise<{
  profile: Record<string, unknown> | null
  tasks: Record<string, unknown>[]
  habits: Record<string, unknown>[]
  routines: Record<string, unknown>[]
  projects: Record<string, unknown>[]
} | null> {
  if (!isApiConfigured()) return null

  try {
    return await api.get('/api/sync/all')
  } catch (error) {
    console.error('[Sync] Failed to fetch user data:', error)
    return null
  }
}

export function getLastSyncTime(): string | null {
  return localStorage.getItem(LAST_SYNC_KEY)
}

export function getPendingSyncCount(): number {
  return getSyncQueue().length
}

// ============================================
// Update queued items with user ID
// ============================================

export function updateQueuedItemsWithUserId(userId: string): void {
  if (!isValidUUID(userId)) {
    console.warn('[Sync] Cannot update queue with invalid user ID:', userId)
    return
  }

  // Cache user ID
  setCurrentUserId(userId)

  const queue = getSyncQueue()
  let queueUpdated = 0

  const updatedQueue = queue.map(item => {
    if (item.data.user_id === 'local') {
      queueUpdated++
      return {
        ...item,
        data: {
          ...item.data,
          user_id: userId,
        },
      }
    }
    return item
  })

  if (queueUpdated > 0) {
    console.log(`[Sync] Updated ${queueUpdated} queued items with user ID`)
    saveSyncQueue(updatedQueue)
  }

  // Also update localStorage store data
  const storeKeys = ['waifudo-tasks', 'waifudo-habits', 'waifudo-projects', 'waifudo-routines']

  for (const key of storeKeys) {
    try {
      const stored = localStorage.getItem(key)
      if (!stored) continue

      const data = JSON.parse(stored)
      if (!data?.state) continue

      const arrayKey = Object.keys(data.state).find(k => Array.isArray(data.state[k]))
      if (!arrayKey) continue

      const items = data.state[arrayKey]
      let storeUpdated = 0

      const updatedItems = items.map((item: { id: string; userId: string }) => {
        if (item.userId === 'local') {
          storeUpdated++
          return { ...item, userId }
        }
        return item
      })

      if (storeUpdated > 0) {
        console.log(`[Sync] Updated ${storeUpdated} items in ${key} with user ID`)
        data.state[arrayKey] = updatedItems
        localStorage.setItem(key, JSON.stringify(data))
      }
    } catch (e) {
      console.error(`[Sync] Failed to update ${key}:`, e)
    }
  }
}

// ============================================
// Cleanup legacy data
// ============================================

export function cleanupLegacyData(): void {
  const storeKeys = ['waifudo-tasks', 'waifudo-habits', 'waifudo-projects', 'waifudo-routines']

  for (const key of storeKeys) {
    try {
      const stored = localStorage.getItem(key)
      if (!stored) continue

      const data = JSON.parse(stored)
      if (!data?.state) continue

      const arrayKey = Object.keys(data.state).find(k => Array.isArray(data.state[k]))
      if (!arrayKey) continue

      const items = data.state[arrayKey]
      const validItems = items.filter((item: { id: string }) => isValidUUID(item.id))

      if (validItems.length < items.length) {
        console.log(`[Cleanup] Removed ${items.length - validItems.length} invalid items from ${key}`)
        data.state[arrayKey] = validItems
        localStorage.setItem(key, JSON.stringify(data))
      }
    } catch (e) {
      console.error(`[Cleanup] Failed to clean ${key}:`, e)
    }
  }

  const queue = getSyncQueue()
  const validQueue = queue.filter(item => isValidUUID(item.id))
  if (validQueue.length < queue.length) {
    console.log(`[Cleanup] Removed ${queue.length - validQueue.length} invalid items from sync queue`)
    saveSyncQueue(validQueue)
  }
}

// ============================================
// Auto-sync on online
// ============================================

let isProcessing = false

export function initAutoSync(): () => void {
  const handleOnline = async () => {
    if (isProcessing) return
    isProcessing = true

    console.log('[Sync] Back online, processing queue...')
    await processQueue()

    isProcessing = false
  }

  window.addEventListener('online', handleOnline)

  if (navigator.onLine) {
    handleOnline()
  }

  return () => {
    window.removeEventListener('online', handleOnline)
  }
}

// ============================================
// Merge strategies
// ============================================

export function mergeData<T extends { id: string; completedAt?: string | null; createdAt?: string }>(
  local: T[],
  remote: T[]
): T[] {
  const merged = new Map<string, T>()

  for (const item of local) {
    merged.set(item.id, item)
  }

  for (const item of remote) {
    const existing = merged.get(item.id)
    if (!existing) {
      merged.set(item.id, item)
    } else {
      const localTime = existing.completedAt || existing.createdAt || ''
      const remoteTime = item.completedAt || item.createdAt || ''

      if (remoteTime > localTime) {
        merged.set(item.id, item)
      }
    }
  }

  return Array.from(merged.values())
}
